import _ from 'lodash';
import Logger from '../util/Logger';

/**
 * Methods for consistent access to TenantStats properties via server-returned JSON.
 *
 * All interactions or accesses on TenantStats fields should be handled via helper method in this
 * class. Methods should only be to avoid `this` scoping issues in nested calls.

 * Utility methods should throw in the case of unexpected constant mismatch such as missing data,
 * all calls to these methods should be guarded at the callsite.
 */
export default {

  /**
   * Extracts the usage_by_device field from a given tenantStats json object
   *
   * @param tenantStats
   * @returns [List of objects with a label and activity count]:
   *   {
   *     "label": "Mobile Web",
   *    "value": 11567
   *   }
   *
   */
  getUsageByDevice (tenantStats) {
    if (!tenantStats) {
      Logger.assert('Cannot call getUsageByDevice() without a tenantStats object');
    } else {
      const usage = _.get(tenantStats, 'usage_by_device');
      if (!usage) {
        Logger.assert('Tenant was missing usage_by_device');
      } else {
        Logger.info("usage: ", usage);
        return usage;
      }
    }
  },

  /**
   * Extracts the questions_by_use_case field from a given tenantStats json object
   *
   * @param tenantStats
   * @returns [List of objects with a label and activity count]:
   *   {
   *     "label": "Meeting",
   *    "value": 167
   *   }
   *
   */
  getPopinsByUseCase (tenantStats) {
    if (!tenantStats) {
      Logger.assert('Cannot call getPopinsByDevice() without a tenantStats object');
    } else {
      const usage = _.get(tenantStats, 'popins_by_use_case');
      if (!usage) {
        Logger.assert('Tenant was missing popins_by_use_case');
      } else {
        return usage;
      }
    }
  },

  getTotalUsers (tenantStats) {
    if (!tenantStats) {
      Logger.assert('Cannot call getTotalUsers() without a tenantStats object');
    } else {
      return _.get(tenantStats, 'total_participants') || 0;
    }
  },

  getTotalActivities (tenantStats) {
    if (!tenantStats) {
      Logger.assert('Cannot call getTotalActivities() without a tenantStats object');
    } else {
      return _.get(tenantStats, 'total_activities') || 0;
    }
  },

  getTotalHeroes (tenantStats) {
    if (!tenantStats) {
      Logger.assert('Cannot call getTotalHeroes() without a tenantStats object');
    } else {
      return _.get(tenantStats, 'total_heroes') || 0;
    }
  },

  getTotalPopins (tenantStats) {
    if (!tenantStats) {
      Logger.assert('Cannot call getTotalPopins() without a tenantStats object');
    } else {
      return _.get(tenantStats, 'total_popins') || 0;
    }
  },

  getDiffCountUsers (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const current = this.getTotalUsers(stats);
    const previous = this.getTotalUsers(prevStats);
    return current - previous;
  },
  getDiffCountHeroes (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const current = this.getTotalHeroes(stats);
    const previous = this.getTotalHeroes(prevStats);
    return current - previous;
  },
  getDiffCountPopins (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const current = this.getTotalPopins(stats);
    const previous = this.getTotalPopins(prevStats);
    if (!previous) {
      return 0;
    }
    return current - previous;
  },
  getDiffCountActivities (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const current = this.getTotalActivities(stats);
    const previous = this.getTotalActivities(prevStats);
    if (!previous) {
      return 0;
    }
    return current - previous;
  },
  getDiffPercentUsers (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const diff = this.getDiffCountUsers(stats, prevStats);
    const previous = this.getTotalUsers(prevStats);
    if (!previous) {
      return 0;
    }
    const diffPercent = ((diff / previous) * 100).toFixed(0);
    return diffPercent;
  },
  getDiffPercentHeroes (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const diff = this.getDiffCountHeroes(stats, prevStats);
    const previous = this.getTotalHeroes(prevStats);
    if (!previous) {
      return 0;
    }
    const diffPercent = ((diff / previous) * 100).toFixed(0);
    return diffPercent;
  },
  getDiffPercentPopins (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const diff = this.getDiffCountPopins(stats, prevStats);
    const previous = this.getTotalPopins(prevStats);
    if (!previous) {
      return 0;
    }
    const diffPercent = ((diff / previous) * 100).toFixed(0);
    return diffPercent;
  },
  getDiffPercentActivities (stats, prevStats) {
    // Stats prev can be null
    if (!prevStats) {
      return 0;
    }

    const diff = this.getDiffCountActivities(stats, prevStats);
    const previous = this.getTotalActivities(prevStats);
    if (!previous) {
      return 0;
    }
    const diffPercent = ((diff / previous) * 100).toFixed(0);
    return diffPercent;
  }
};
